import { ApiResponse } from "@/types";
import { apiSlice } from "@/redux/apiSlice";
import { Consultation, ConsultationTable, FormQuestionResponse, BodyMeasurementCreate, BodyMeasurementResponse, BodyMeasurementListResponse, ConsultationCreate, ConsultationAnswerResponse, GroupedQuestionsResponse, GroupedAnswersResponse, ConsultationAnswer, BodyAnalysis, BodyAnalysisResponse, PostureAnalysis, PostureAnalysisResponse, ConsultationGoal, GoalResponse, GoalEvaluationBatch, AllGoalEvaluationsResponse, HistoryConsultation, HistoryResponse } from "@/types/consultation";
import consultationMockData from "@/mock/consultation-data.json";
import { ConsultationStatus } from "@/types/enums";

export const ConsultationApi = apiSlice.injectEndpoints({
  endpoints(builder) {
    return {
      getQuestionTypes: builder.query<string[], void>({
        query: () => ({
          url: `/question-types`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Consultation"],
      }),

      getSectionKeys: builder.query<string[], void>({
        query: () => ({
          url: `/section-keys`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Consultation"],
      }),

      
      // Consultation Endpoints
      getConsultations: builder.query<
        ConsultationTable,
        { query: string; orgId: number }
      >({
        query: ({ query, orgId }) => ({
          url: `/consultations?org_id=${orgId}${query ? `&${query}` : ""}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: (result, error, { orgId }) => [
          { type: "Consultation", id: `LIST_${orgId}` },
          "Consultation",
        ],
      }),

      getConsultationById: builder.query<
        Consultation,
        {
          org_id: number;
          id: number;
          query?: string;
        }
      >({
        query: ({ id, org_id, query }) => ({
          url: `/consultations/${id}?org_id=${org_id}${
            query && query.length > 0 ? `&${query}` : ""
          }`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: (result, error, { id, org_id }) => [
          { type: "Consultation", id: `${org_id}_${id}` },
          "Consultation",
        ],
        // Immediately drop cache when there are no active subscribers
        keepUnusedDataFor: 0,
      }),

      createConsultation: builder.mutation<ApiResponse, ConsultationCreate>({
        query: (consultation) => ({
          url: `/consultations?org_id=${consultation.org_id}`,
          method: "POST",
          body: consultation,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Consultation"],
      }),

      updateConsultation: builder.mutation<ApiResponse, Consultation & { org_id: number }>({
        query: (consultation) => ({
          url: `/consultations/${consultation.id}?org_id=${consultation.org_id}`,
          method: "PUT",
          body: consultation,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Consultation"],
      }),
      // patch consultation status
      patchConsultationStatus: builder.mutation<ApiResponse, { consultation_id: number; org_id: number; status: ConsultationStatus }>({
        query: ({ consultation_id, org_id, status }) => ({
          url: `/consultations/${consultation_id}/status?org_id=${org_id}&status_update=${status}`,
          method: "PATCH",
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Consultation"],
      }),

      getConsultationAnswers: builder.query<GroupedAnswersResponse, { consultation_id: number; org_id: number; section_key?: string[] }>({
        query: ({ consultation_id, org_id, section_key }) => {
          const params = new URLSearchParams();
          params.append("org_id", org_id.toString());
          if (section_key) {
            section_key.forEach((key) => params.append("section_key", key));
          }
          return {
            url: `/consultations/${consultation_id}/answers?${params.toString()}`,
            method: "GET",
          };
        },
        providesTags: ["Consultation"],
      }),

      submitConsultationAnswers: builder.mutation<ApiResponse, { consultation_id: number; org_id: number; answers: ConsultationAnswer[] }>({
        query: ({ consultation_id, org_id, answers }) => ({
          url: `/consultations/${consultation_id}/answers?org_id=${org_id}`,
          method: "POST",
          body: { answers },
          headers: {
            Accept: "application/json",
          },
        }),
       invalidatesTags: ["Consultation"],
      }),

      deleteConsultation: builder.mutation<ApiResponse, { id: number; org_id: number }>({
        query: ({ id, org_id }) => ({
          url: `/consultations/${id}?org_id=${org_id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Consultation"],
      }),

      getQuestions: builder.query<GroupedQuestionsResponse, { org_id: number; section_key?: string[] }>({
        query: ({ org_id, section_key }) => {
          const params = new URLSearchParams();
          params.append("org_id", org_id.toString());
          if (section_key) {
            section_key.forEach((key) => params.append("section_key", key));
          }
          const queryString = params.toString();
          return {
            url: `/questions${queryString ? `?${queryString}` : ""}`,
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          };
        },
        providesTags: ["Consultation"],
      }),

      createMeasurement: builder.mutation<
        BodyMeasurementResponse,
        { consultation_id: number; org_id: number; data: BodyMeasurementCreate }
      >({
        query: ({ consultation_id, org_id, data }) => ({
          url: `/consultations/${consultation_id}/measurements?org_id=${org_id}`,
          method: "POST",
          body: { body_measurement: data },
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Consultation"],
      }),

      getMeasurements: builder.query<
        BodyMeasurementListResponse,
        { consultation_id: number, org_id: number }
      >({
        query: ({ consultation_id, org_id }) => ({
          url: `/consultations/${consultation_id}/measurements?org_id=${org_id}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Consultation"],
      }),
      createBodyAnalysis: builder.mutation<
        BodyAnalysisResponse,
        { consultation_id: number; org_id: number; data: BodyAnalysis }
      >({
        query: ({ consultation_id, org_id, data }) => ({
          url: `/consultations/${consultation_id}/body-analysis?org_id=${org_id}`,
          method: "POST",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Consultation"],
      }),
      getBodyAnalysis: builder.query<
        BodyAnalysisResponse,
        { consultation_id: number; org_id: number }
      >({
        query: ({ consultation_id, org_id }) => ({
          url: `/consultations/${consultation_id}/body-analysis?org_id=${org_id}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Consultation"],
      }),
      updateBodyAnalysis: builder.mutation<
        BodyAnalysisResponse,
        { consultation_id: number; org_id: number; data: BodyAnalysis }
      >({
        query: ({ consultation_id, org_id, data }) => ({
          url: `/consultations/${consultation_id}/body-analysis?org_id=${org_id}`,
          method: "PUT",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Consultation"],
      }),
      createPostureAnalysis: builder.mutation<
        PostureAnalysisResponse,
        { consultation_id: number; org_id: number; data: PostureAnalysis }
      >({
        query: ({ consultation_id, org_id, data }) => ({
          url: `/consultations/${consultation_id}/posture-analysis?org_id=${org_id}`,
          method: "POST",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Consultation"],
      }),
      getPostureAnalysis: builder.query<
        PostureAnalysisResponse,
        { consultation_id: number; org_id: number }
      >({
        query: ({ consultation_id, org_id }) => ({
          url: `/consultations/${consultation_id}/posture-analysis?org_id=${org_id}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Consultation"],
      }),
      updatePostureAnalysis: builder.mutation<
        PostureAnalysisResponse,
        { consultation_id: number; org_id: number; data: PostureAnalysis }
      >({
        query: ({ consultation_id, org_id, data }) => ({
          url: `/consultations/${consultation_id}/posture-analysis?org_id=${org_id}`,
          method: "PUT",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Consultation"],
      }),
      createGoal: builder.mutation<
        GoalResponse,
        { consultation_id: number; org_id: number; data: ConsultationGoal[] }
      >({
        query: ({ consultation_id, org_id, data }) => ({
          url: `/consultations/${consultation_id}/goals?org_id=${org_id}`,
          method: "POST",
          body: { goals: data },
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Consultation"],
      }),
      getGoal: builder.query<
        { consultation_id: number; goals: ConsultationGoal[] }, 
        { consultation_id: number; org_id: number }
      >({
        query: ({ consultation_id, org_id }) => ({
          url: `/consultations/${consultation_id}/goals?org_id=${org_id}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),

        providesTags: ["Consultation"],
      }),
      updateGoal: builder.mutation<
        GoalResponse,
        { consultation_id: number; org_id: number; data: ConsultationGoal[] }
      >({
        query: ({ consultation_id, org_id, data }) => ({
          url: `/consultations/${consultation_id}/goals?org_id=${org_id}`,
          method: "PUT",
          body: { goals: data },
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Consultation"],
      }),
      assignMealPlan: builder.mutation<
        void,
        { consultation_id: number; org_id: number; data: { meal_plan_ids: number[] } }
      >({
        query: ({ consultation_id, org_id, data }) => ({
          url: `/consultations/${consultation_id}/meal-plan?org_id=${org_id}`,
          method: "PATCH",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Consultation"],
      }),
      assignWorkoutPlan: builder.mutation<
        void,
        { consultation_id: number; org_id: number; data: { workout_plan_ids: number[] } }
      >({
        query: ({ consultation_id, org_id, data }) => ({
          url: `/consultations/${consultation_id}/workout-plan?org_id=${org_id}`,
          method: "PATCH",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Consultation"],
      }),
      createGoalEvaluation: builder.mutation<
        void,
        { consultation_id: number; org_id: number; data: GoalEvaluationBatch }
      >({
        query: ({ consultation_id, org_id, data }) => ({
          url: `/consultations/${consultation_id}/goal-evaluations?org_id=${org_id}`,
          method: "POST",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Consultation"],
      }),
      updateGoalEvaluation: builder.mutation<
        void,
        { consultation_id: number; org_id: number; data: GoalEvaluationBatch }
      >({
        query: ({ consultation_id, org_id, data }) => ({
          url: `/consultations/${consultation_id}/goal-evaluations?org_id=${org_id}`,
          method: "PUT",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Consultation"],
      }),
      getAllGoalEvaluations: builder.query<
        AllGoalEvaluationsResponse,
        { consultation_id: number; org_id: number }
      >({
        query: ({ consultation_id, org_id }) => ({
          url: `/consultations/${consultation_id}/all-goal-evaluations?org_id=${org_id}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Consultation"],
      }),
      deleteGoalEvaluation: builder.mutation<
        void,
        { consultation_id: number; goal_id: number; org_id: number }
      >({
        query: ({ consultation_id, goal_id, org_id }) => ({
          url: `/consultations/${consultation_id}/goal-evaluations/${goal_id}?org_id=${org_id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Consultation"],
      }),
      deleteGoal: builder.mutation<
        void,
        { consultation_id: number; goal_id: number; org_id: number }
      >({
        query: ({ consultation_id, goal_id, org_id }) => ({
          url: `/consultations/${consultation_id}/goals/${goal_id}?org_id=${org_id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Consultation"],
      }),
      getHistoryConsultation: builder.query<
        HistoryResponse,
        { memberId: number; month: string; year: string; orgId: number }
      >({
        query: ({ memberId, month, year, orgId }) => ({
          url: `/consultations/${memberId}/month/${month}/year/${year}?org_id=${orgId}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Consultation"],
      }),
    };
  },
});

export const {
  useGetQuestionTypesQuery,
  useGetSectionKeysQuery,
  useGetConsultationsQuery,
  useGetConsultationByIdQuery,
  useCreateConsultationMutation,
  useUpdateConsultationMutation,
  useDeleteConsultationMutation,
  useSubmitConsultationAnswersMutation,
  useGetQuestionsQuery,
  useGetConsultationAnswersQuery,
  usePatchConsultationStatusMutation,
  useCreateMeasurementMutation,
  useGetMeasurementsQuery,
  useCreateBodyAnalysisMutation,
  useGetBodyAnalysisQuery,
  useUpdateBodyAnalysisMutation,
  useCreatePostureAnalysisMutation,
  useGetPostureAnalysisQuery,
  useUpdatePostureAnalysisMutation,
  useCreateGoalMutation,
  useGetGoalQuery,
  useUpdateGoalMutation,
  useAssignMealPlanMutation,
  useAssignWorkoutPlanMutation,
  useCreateGoalEvaluationMutation,
  useUpdateGoalEvaluationMutation,
  useGetAllGoalEvaluationsQuery,
  useDeleteGoalEvaluationMutation,
  useDeleteGoalMutation,
  useGetHistoryConsultationQuery,
} = ConsultationApi;

